import { useCurrentMode } from '@/hooks/useCurrentMode';
import styles from './ModeIndicatorPill.module.css';

/**
 * Fleet Mode Phase 3 — persistent top-nav mode indicator.
 *
 * Renders a small pill ("FLEET" / "PUBLIC") that surfaces the
 * driver's active mode at a glance from any PWA chrome surface.
 *
 *   - Self-hides when the user has no fleet attribution (`canSwitch`
 *     is false). Non-fleet drivers never see the pill — the mode
 *     mechanism is meaningless for them, so the chrome stays
 *     uncluttered.
 *   - mode === 'fleet' → blue (--evi-info). Chosen over --evi-busy
 *     so the amber "active session" semantic doesn't collide with
 *     the orthogonal "you're in fleet mode" identity signal. No
 *     new --evi-fleet token introduced — Phase 1's existing
 *     palette already covers this.
 *   - mode === 'public' → brand green (--evi-accent). Matches the
 *     status-pill primitive's "accent" variant so the visual
 *     vocabulary lines up across the app.
 *
 * Tap navigates to /profile (where the Fleet Mode toggle lives)
 * rather than firing the toggle itself. Pill is INFORMATIONAL; the
 * toggle is a DELIBERATE action in Profile + Account, gated by the
 * status pill's own affordance. One-tap-from-chrome toggling would
 * make accidental flips too easy on the most-trafficked surface.
 */
interface Props {
  onNavigate: (path: string) => void;
}

export function ModeIndicatorPill({ onNavigate }: Props) {
  const { mode, canSwitch } = useCurrentMode();
  if (!canSwitch) return null;

  const isFleet = mode === 'fleet';
  return (
    <button
      type="button"
      className={`${styles.pill} ${isFleet ? styles.pillFleet : styles.pillPublic}`}
      onClick={() => onNavigate('/profile')}
      aria-label={`Current mode: ${isFleet ? 'fleet' : 'public'}. Tap to manage.`}
    >
      <span aria-hidden className={styles.dot} />
      <span className={styles.label}>{isFleet ? 'FLEET' : 'PUBLIC'}</span>
    </button>
  );
}
